import { useCallback, useRef, useState } from 'react';
import type { DragEvent } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import { FiUploadCloud } from 'react-icons/fi';
import { ACCEPTED_EXT, MAX_VIDEO_BYTES, formatBytes, isOversizedVideoFile, isSupportedVideoFile } from '../utils/videoOps';

const FORMAT_PILLS = ['MP4','MOV','WEBM','M4V','MKV','AVI'];

interface DropzoneProps {
  onFilesAdded: (files: File[]) => void;
}

const Dropzone = ({ onFilesAdded }: DropzoneProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [rejection, setRejection] = useState<string | null>(null);

  const handleFiles = useCallback(
    (fileList: FileList | File[] | null) => {
      if (!fileList) return;
      const files = Array.from(fileList);
      const arr = files.filter(isSupportedVideoFile);
      const oversized = files.filter(isOversizedVideoFile).length;
      const unsupported = files.length - arr.length - oversized;
      if (oversized || unsupported) {
        const parts = [
          oversized ? `${oversized} over ${formatBytes(MAX_VIDEO_BYTES)}` : null,
          unsupported ? `${unsupported} unsupported` : null,
        ].filter(Boolean);
        setRejection(`Skipped ${parts.join(' and ')} file${oversized + unsupported > 1 ? 's' : ''}.`);
      } else {
        setRejection(null);
      }
      if (arr.length) onFilesAdded(arr);
    },
    [onFilesAdded]
  );

  const handleDrop = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setIsDragging(false);
      if (e.dataTransfer.files) handleFiles(e.dataTransfer.files);
    },
    [handleFiles]
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.15 }}
      className="w-full max-w-3xl mx-auto mb-10"
    >
      <div
        role="button"
        tabIndex={0}
        aria-label="Add videos to convert"
        onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            fileInputRef.current?.click();
          }
        }}
        className={`glass group cursor-pointer border-2 border-dashed rounded-3xl md:rounded-[2rem] p-8 sm:p-10 md:p-14 text-center transition-[border-color,background-color,box-shadow,transform] outline-none focus-visible:ring-2 focus-visible:ring-teal-500 select-none touch-manipulation ${
          isDragging
            ? 'border-lime-500 bg-lime-50/60 dark:bg-lime-900/10 scale-[1.01] shadow-xl shadow-lime-500/10'
            : 'border-slate-300 dark:border-slate-700 hover:border-lime-500 dark:hover:border-lime-500 hover:shadow-lg'
        }`}
      >
        <input
          type="file"
          multiple
          accept={ACCEPTED_EXT.join(',')}
          className="hidden"
          ref={fileInputRef}
          onChange={(e) => {
            handleFiles(e.target.files);
            e.target.value = '';
          }}
        />

        <motion.div
          animate={{ scale: isDragging ? 1.2 : 1 }}
          transition={{ type: 'spring', stiffness: 300 }}
          className="bg-gradient-to-br from-teal-100 to-lime-100 dark:from-teal-900/50 dark:to-lime-900/40 w-20 h-20 rounded-3xl flex items-center justify-center mx-auto mb-5 shadow-inner"
        >
          <SafeIcon icon={FiUploadCloud} className="text-4xl text-lime-700 dark:text-lime-300" />
        </motion.div>

        <h2 className="text-2xl md:text-3xl font-black mb-2 text-slate-800 dark:text-white">
          {isDragging ? 'Release to add files' : 'Drop your videos here'}
        </h2>
        <p className="text-slate-500 dark:text-slate-400 text-sm mb-6">
          or <span className="text-teal-600 dark:text-teal-300 font-bold underline underline-offset-2">click to browse</span> your files
        </p>

        <div className="flex flex-wrap justify-center gap-2">
          {FORMAT_PILLS.map((fmt) => (
            <span
              key={fmt}
              className="text-[11px] font-bold uppercase tracking-wider px-3 py-1 rounded-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-500 dark:text-slate-400"
            >
              {fmt}
            </span>
          ))}
        </div>

        <p className="text-[11px] text-slate-400 mt-4">
          Private by design · Videos up to {formatBytes(MAX_VIDEO_BYTES)} stay on your device
        </p>
        {rejection && (
          <p className="text-xs font-bold text-amber-600 dark:text-amber-300 mt-3" role="status">
            {rejection}
          </p>
        )}
      </div>
    </motion.div>
  );
};

export default Dropzone;
